<template>
  <Dialog
    :open="true"
    @update:open="
      val => {
        if (!val) handleCancel();
      }
    "
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Onboard candidate</DialogTitle>
        <DialogDescription>
          <template v-if="contact.stage === 'Onboarding'">
            This will regenerate their onboarding agreement, useful if you've updated their details.
          </template>
          <template v-else> This will send the candidate an email to begin joining your brokerage/team. </template>
        </DialogDescription>
      </DialogHeader>

      <div v-if="loaded && onboardingChecklists.length > 0" class="space-y-3">
        <Separator />
        <div class="space-y-2">
          <Label class="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Assign checklist
            <Badge variant="secondary" class="ml-1.5 text-[10px] px-1.5 py-0">Optional</Badge>
          </Label>
          <Select v-model="selectedTaskChecklistId">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem v-for="checklist in onboardingChecklists" :key="checklist.id" :value="String(checklist.id)">
                {{ checklist.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter class="pt-2">
        <DialogClose as-child>
          <Button variant="outline" @click="handleCancel">Cancel</Button>
        </DialogClose>
        <Button :disabled="submitting" @click="handleOnboard">
          <Loader2 v-if="submitting" class="h-4 w-4 animate-spin mr-1.5" />
          Onboard
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { toast } from 'vue-sonner';
import { Api } from '@/api';
import { Loader2 } from 'lucide-vue-next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type OnboardingChecklist = {
  id: number;
  name: string;
  status: 'enabled' | 'disabled';
};

type Props = {
  contact: {
    id: number;
    stage: string | number;
  };
};

const props = defineProps<Props>();

const emit = defineEmits<{
  handleContactOnboard: [];
  handleContactOnboardCancel: [];
}>();

const onboardingChecklists = ref<OnboardingChecklist[]>([]);
const selectedTaskChecklistId = ref<string>('none');
const loaded = ref(false);
const submitting = ref(false);

const fetchOnboardingChecklists = async (): Promise<void> => {
  const res = await fetch('/api/task_checklists?checklist_type=onboarding');
  const { records } = await res.json();
  onboardingChecklists.value = records.filter((c: OnboardingChecklist) => c.status === 'enabled');
};

const handleOnboard = async (): Promise<void> => {
  submitting.value = true;

  try {
    const response = await Api.Contacts.onboardContact({
      contactId: props.contact.id,
      selectedTaskChecklistId:
        selectedTaskChecklistId.value && selectedTaskChecklistId.value !== 'none'
          ? Number(selectedTaskChecklistId.value)
          : undefined,
    });

    if (response.status === 'success') {
      emit('handleContactOnboard');
      toast.success('Contact onboarding started');
    } else {
      toast.error('Contact onboarding error', {
        description: response.message,
      });
    }
  } finally {
    submitting.value = false;
  }
};

const handleCancel = (): void => {
  emit('handleContactOnboardCancel');
};

onMounted(async () => {
  await fetchOnboardingChecklists();
  loaded.value = true;
});
</script>
